import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import type {
  ExistingUserProfileSnapshot,
  LoadExistingUserProfileInput,
  LoadExistingUserProfileResult,
} from '../../../domain/commerce-registry/models/existing-user-profile.model';
import { ExistingUserProfilePort } from '../../../domain/commerce-registry/ports/existing-user-profile.port';
import { ENVIRONMENT } from '../../../../environments/environment';

const CODE_OK = 100;

/**
 * Public gate token for SAAS `POST .../validar_perfil_cedula` on dynamic landings (no email step yet).
 * Same shape as legacy body `{ cedula, token }`.
 */
const PROFILE_VALIDATE_PUBLIC_TOKEN = '7b7d';

interface ValidarPerfilCedulaApiResponse {
  code?: number;
  message?: string;
  data?: unknown;
}

interface PerfilUsuarioApiData {
  uid?: string;
  email?: string;
  cedula?: string;
  tipoIdentificacion?: number;
  tipoIdentificacionSiglas?: string;
  celular?: string;
  genero?: string;
  nombre?: string;
  apellido?: string;
  cumpleanos?: string;
  idCiudad?: number;
  codCiudad?: number;
}

interface PerfilUsuarioApiResponse {
  code?: number;
  message?: string;
  data?: PerfilUsuarioApiData | null;
}

@Injectable()
export class ExistingUserProfileApiAdapter extends ExistingUserProfilePort {
  constructor(private readonly http: HttpClient) {
    super();
  }

  load(input: LoadExistingUserProfileInput): Observable<LoadExistingUserProfileResult> {
    const id = input.uid?.trim();
    if (!id) {
      return of<LoadExistingUserProfileResult>({
        outcome: 'failure',
        message: 'Identificador de usuario no válido',
      });
    }

    const base = ENVIRONMENT.apiMobileUrl?.trim();
    if (!base) {
      return of<LoadExistingUserProfileResult>({
        outcome: 'failure',
        message: 'Servicio de perfil no configurado',
      });
    }

    const root = base.replace(/\/$/, '');
    const cedula = input.cedula?.trim() ?? '';
    if (!cedula) {
      // GET perfil requires Bearer; JWT only comes from validar_perfil_cedula (needs document/celular).
      return of<LoadExistingUserProfileResult>({
        outcome: 'failure',
        message: 'Documento o celular requerido para cargar el perfil',
      });
    }
    return this.loadWithCedulaGate(root, id, cedula);
  }

  private loadWithCedulaGate(
    root: string,
    uid: string,
    cedula: string,
  ): Observable<LoadExistingUserProfileResult> {
    const validateUrl = `${root}/api/usuarios/validar_perfil_cedula`;

    return this.http
      .post<ValidarPerfilCedulaApiResponse>(validateUrl, {
        cedula,
        token: PROFILE_VALIDATE_PUBLIC_TOKEN,
      })
      .pipe(
        switchMap((res) => this.loadPerfilWithJwt(root, uid, res)),
        catchError(() =>
          of<LoadExistingUserProfileResult>({
            outcome: 'failure',
            message: 'No pudimos validar el acceso al perfil. Intenta de nuevo.',
          }),
        ),
      );
  }

  private loadPerfilWithJwt(
    root: string,
    uid: string,
    res: ValidarPerfilCedulaApiResponse,
  ): Observable<LoadExistingUserProfileResult> {
    const code = res?.code;
    const message = typeof res?.message === 'string' ? res.message.trim() : '';
    if (code !== CODE_OK || typeof res.data !== 'string' || !res.data.trim()) {
      return of<LoadExistingUserProfileResult>({
        outcome: 'failure',
        message: message || 'No se pudo validar el acceso al perfil',
      });
    }
    const jwt = res.data.trim();
    const perfilUrl = `${root}/api/usuarios/${encodeURIComponent(uid)}/perfil`;
    return this.http
      .get<PerfilUsuarioApiResponse>(perfilUrl, {
        headers: new HttpHeaders({ Authorization: `Bearer ${jwt}` }),
      })
      .pipe(
        map((prof) => this.mapResponse(prof, jwt)),
        catchError(() =>
          of<LoadExistingUserProfileResult>({
            outcome: 'failure',
            message: 'No pudimos obtener tu perfil. Intenta de nuevo.',
          }),
        ),
      );
  }

  private mapResponse(res: PerfilUsuarioApiResponse, perfilAccessToken: string): LoadExistingUserProfileResult {
    const code = res?.code;
    const message = typeof res?.message === 'string' ? res.message.trim() : '';
    if (code !== CODE_OK || !res?.data || typeof res.data !== 'object') {
      return {
        outcome: 'failure',
        message: message || 'No se pudo cargar el perfil',
      };
    }
    const profile = this.toSnapshot(res.data);
    if (!profile) {
      return {
        outcome: 'failure',
        message: message || 'Respuesta de perfil incompleta',
      };
    }
    return { outcome: 'success', profile, perfilAccessToken };
  }

  private toSnapshot(data: PerfilUsuarioApiData): ExistingUserProfileSnapshot | null {
    const uid = typeof data.uid === 'string' ? data.uid.trim() : '';
    const email = typeof data.email === 'string' ? data.email.trim() : '';
    const cedula = typeof data.cedula === 'string' ? data.cedula.trim() : '';
    const celular = typeof data.celular === 'string' ? data.celular.trim() : '';
    const genero = typeof data.genero === 'string' ? data.genero.trim() : '';
    const nombre = typeof data.nombre === 'string' ? data.nombre.trim() : '';
    const apellido = typeof data.apellido === 'string' ? data.apellido.trim() : '';
    const cumpleanosIso = typeof data.cumpleanos === 'string' ? data.cumpleanos.trim() : '';

    if (!uid || !email || !nombre || !apellido || !cumpleanosIso) {
      return null;
    }

    return {
      uid,
      email,
      cedula,
      tipoIdentificacion: data.tipoIdentificacion,
      tipoIdentificacionSiglas:
        typeof data.tipoIdentificacionSiglas === 'string' ? data.tipoIdentificacionSiglas.trim() : undefined,
      celular,
      genero,
      nombre,
      apellido,
      cumpleanosIso,
      idCiudad: typeof data.idCiudad === 'number' ? data.idCiudad : undefined,
      codCiudad: typeof data.codCiudad === 'number' ? data.codCiudad : undefined,
    };
  }
}
